import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TYPE_TO_REMOVE } from 'src/app/services/dmp/dmp-service.abstract';
import { contentTypeCodeSettingKey } from 'src/app/services/dmp/dmp.service';
import { ReferentielService } from 'src/app/services/referentiel/referentiel.service';
import { RessourceService } from 'src/app/services/ressource/ressource.service';
import { MSSClinicalDocumentInfo } from 'src/app/utils/interfaces/new/mss-clinical-document-info.interface';
import { documentClass, documentType, documentFormat } from 'src/app/services/ressource/ressource.service';
import { PieceJointe } from 'src/app/fragments/modal/quick-send-mss/quick-send-mss.fragment';
import { DocumentService } from 'src/app/services/document/document.service';
import { Courrier } from 'src/app/utils/interfaces/new/Courrier';
import { CourriersService } from 'src/app/services/consultation/courriers/courriers.service';
import { TexteModele } from 'src/app/utils/interfaces/new/TexteModele';
import { TexteService } from 'src/app/services/texte/texte.service';

export interface JDVValue {
  id: string;
  libelle: string;
}

@Component({
  selector: 'app-presend-clinicaldocument-modal',
  templateUrl: './presend-clinicaldocument-modal.component.html',
  styleUrls: [
    './presend-clinicaldocument-modal.component.scss',
    '../../../../../fragments/modal/modal.fragment.scss',
  ],
})
export class PresendClinicalDocumentModalComponent implements OnInit {
  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  public formGroup: FormGroup = this._formBuilder.group({
    titreDocument: ['', [Validators.required]],
    descriptionDocument: [''],
    classDocument: [undefined, [Validators.required]], // classCode : documentClassCode
    typeDocument: [undefined, [Validators.required]], // typeCode : documentTypeCode
    formatDocument: [undefined, [Validators.required]], // formatCode : documentFormatCode
    typeActivite: [undefined, [Validators.required]], // typeActivite : activityTypeCode
  });
public typeDocument: string | null = null; //type de document qui vient de edit-courrier car on l'a déjà récupéré à ce moment là

  public typeDocuments: Array<JDVValue> = [];
  public typeDocumentsTableName = 'RefTypeCodeDMP';
  public classDocuments: Array<JDVValue> = [];
  public classDocumentsTableName = 'RefClassCodeDMP';
  public formatDocuments: Array<JDVValue> = [];
  public formatDocumentsTableName = 'RefFormatCodeDMP';
  public typeActivites: Array<JDVValue> = [];
  public typeActivitesTableName = 'RefContentTypeCodeDMP';

  @Output() public closeEmitter = new EventEmitter<
    MSSClinicalDocumentInfo | undefined
  >();
  @Output() public initOverEmitter = new EventEmitter<void>();
  public idTexteModele: string; // id du texte modèle à partir duquel le courrier a été créé

  constructor(
    private _formBuilder: FormBuilder,
    private _refSrv: ReferentielService,
    private _resourceSrv: RessourceService,
    private _texteSrv: TexteService
  ) {}

  async ngOnInit(): Promise<void> {
    // On récupère les paramètres
    await this.populateNgSelectValues(
      this.typeDocuments,
      this.typeDocumentsTableName
    );
    await this.populateNgSelectValues(
      this.classDocuments,
      this.classDocumentsTableName
    );
    await this.populateNgSelectValues(
      this.formatDocuments,
      this.formatDocumentsTableName
    );
    await this.populateNgSelectValues(
      this.typeActivites,
      this.typeActivitesTableName
    );
    let texteModele: TexteModele = null;
    if (this.idTexteModele) {
      texteModele = (await this._texteSrv.findOneTexte(
        this.idTexteModele
      )) as TexteModele;
    }
    const currentProfilStr = localStorage.getItem('currentProfil');
    if (currentProfilStr && currentProfilStr !== '') {
      const currentProfil = JSON.parse(currentProfilStr);
      if (currentProfil.id && currentProfil.id !== '') {
        const params = await this._resourceSrv.getResourceSettings(
          currentProfil.id,
          'profil'
        );

        const contentTypeCodeSetting = params.find(
          (p) => p.settingKey === contentTypeCodeSettingKey
        );
        if (
          contentTypeCodeSetting &&
          this.formGroup.get('typeActivite') &&
          this.formGroup.get('typeActivite')
        ) {
          this.formGroup
            .get('typeActivite')
            .setValue(contentTypeCodeSetting.settingValue);
        }
        // Si le courrier a été créé à partir d'un texte modèle, on préremplit les champs avec les valeurs du texte modèle
        if (texteModele && texteModele.documentClass) {
          this.formGroup
            .get('classDocument')
            .setValue(texteModele.documentClass);
        } else {
          const documentClassSetting = params.find(
            (p) => p.settingKey === documentClass
          );
          if (documentClassSetting && this.formGroup.get('classDocument')) {
            this.formGroup
              .get('classDocument')
              .setValue(documentClassSetting.settingValue);
          }
        }

        // Déterminer le type de document à sélectionner par défaut:
        let finalType = null;
        
        // Priorité 1 : Valeur transmise par le composant parent
        if (this.typeDocument) {
          const typeDocumentCleaned = this.typeDocument.indexOf('++') > -1 ? this.typeDocument.split('++')[0] : this.typeDocument;
          const matchingOption = this.typeDocuments.find(
            t => t.id === typeDocumentCleaned || t.libelle === typeDocumentCleaned
          );
          if (matchingOption) finalType = matchingOption.id;
        }

        // Priorité 2 (si aucune valeur transmise valide) : le texteModele
        if (!finalType && texteModele && texteModele.documentType) {
          const matchingModeleOption = this.typeDocuments.find(t => t.id === texteModele.documentType);
          if (matchingModeleOption) finalType = texteModele.documentType;
        }

        // Priorité 3 (si toujours aucune valeur valide) : Les paramètres utilisateur
        if (!finalType) {
          const documentTypeSetting = params.find(p => p.settingKey === documentType);
          if (documentTypeSetting) {
            const matchingParamOption = this.typeDocuments.find(t => t.id === documentTypeSetting.settingValue);
            if (matchingParamOption) finalType = documentTypeSetting.settingValue;
          }
        }

        if (this.formGroup.get('typeDocument')) {
          this.formGroup.get('typeDocument').setValue(finalType);
        }
        if (texteModele && texteModele.documentFormat) {
          this.formGroup
            .get('formatDocument')
            .setValue(texteModele.documentFormat);
        } else {
          const documentFormatSetting = params.find(
            (p) => p.settingKey === documentFormat
          );
          if (documentFormatSetting && this.formGroup.get('formatDocument')) {
            this.formGroup
              .get('formatDocument')
              .setValue(documentFormatSetting.settingValue);
          }
        }
      }
    }

    this.initOverEmitter.emit();
  }

  public validate(): void {
    const info: MSSClinicalDocumentInfo = {
      nomDocument: this.formGroup.get('titreDocument').value,
      descriptionDocument: this.formGroup.get('descriptionDocument').value,
      classDocument: this.formGroup.get('classDocument').value,
      typeDocument: this.formGroup.get('typeDocument').value,
      formatDocument: this.formGroup.get('formatDocument').value,
      typeActivite: this.formGroup.get('typeActivite').value,
    };
    this.closeModal(info ? info : undefined);
  }

  public setDocTitle(title: string): void {
    if (this.formGroup.get('titreDocument')) {
      this.formGroup.get('titreDocument').setValue(title);
    }
  }

  public setDocType(type: string): void {
    const split = type.split('++');
    if (split.length > 1) {
      type = split[0];
    }
    if (this.formGroup.get('typeDocument')) {
      this.formGroup.get('typeDocument').setValue(type);
    }
  }

  public setDocFormat(format: string): void {
    const split = format.split('++');
    if (split.length > 1) {
      format = split[0];
    }
    if (this.formGroup.get('formatDocument')) {
      this.formGroup.get('formatDocument').setValue(format);
    }
  }

  public setNonEditableFields(
    fields: Array<
      | 'titreDocument'
      | 'descriptionDocument'
      | 'classDocument'
      | 'typeDocument'
      | 'formatDocument'
      | 'typeActivite'
    >
  ): void {
    for (const field of fields) {
      const f = document.querySelector(`#modal-presend-cda #${field}`);
      if (f) {
        f.classList.add('disabled');
      }
    }
  }

  public closeModal(res?: MSSClinicalDocumentInfo): void {
    this.closeEmitter.emit(res);
  }

  public async populateNgSelectValues(
    targetTable: JDVValue[],
    tableName: string
  ): Promise<void> {
    const temp = await this._refSrv.findRefListTableHypermed(tableName);
      for (const value of temp) {
        const cleanedId = value.id.indexOf('++') > -1 ? value.id.split('++')[0] : value.id;
        if (!TYPE_TO_REMOVE.includes(cleanedId)) {
          targetTable.push({
            id: cleanedId,
            libelle: value.libelle,
          });
        }
      }
  }
}
